import { useEffect, useMemo, useState } from "react";
import { ContractorDetailModal } from "./ContractorDetailModal";
import { ContractorListSkeleton } from "components/shimmer";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Mail,
  MapPin,
  Phone,
  Search,
  Sparkles,
} from "lucide-react";
import { ImageWithFallback } from "components/ui/ImageWithFallback";

const relibuildIcon = "/relibuild-favicon.ico";

interface ContractorExplorerProps {
  contractors: any[];
  loading?: boolean;
  onNext: (contractorIds: string[]) => void;
}

export function ContractorExplorer({
  contractors,
  loading = false,
  onNext,
}: ContractorExplorerProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [profileFilter, setProfileFilter] = useState<string>("All");
  const [locationFilter, setLocationFilter] = useState<string>("All");
  const [ratingFilter, setRatingFilter] = useState<string>("All");
  const [detailContractor, setDetailContractor] = useState<any>(null);

  // ✅ Keep selection valid
  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => contractors.some((c) => c.id === id))
    );
  }, [contractors]);

  // ✅ Profiles
  const allProfiles = useMemo(
    () =>
      Array.from(
        new Set(
          contractors.map((c) => c.profile).filter(Boolean)
        )
      ).sort(),
    [contractors]
  );

  // ✅ Locations
  const allLocations = useMemo(
    () =>
      Array.from(
        new Set(contractors.map((c) => c.location).filter(Boolean))
      ).sort(),
    [contractors]
  );
  // ✅ Filtering
  const filteredContractors = contractors.filter((c) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (normalizedSearch) {
      const serviceText = Array.isArray(c.services)
        ? c.services
            .map((service: any) =>
              typeof service === "string"
                ? service
                : service?.label || service?.name || ""
            )
            .join(" ")
        : "";

      const searchableFields = [
        c.name,
        c.email,
        c.phone,
        c.companyName,
        c.profile,
        c.location,
        c.address,
        c.description,
        serviceText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchableFields.includes(normalizedSearch)) {
        return false;
      }
    }

    if (profileFilter !== "All" && c.profile !== profileFilter) return false;
    if (locationFilter !== "All" && c.location !== locationFilter) return false;

    if (ratingFilter === "4.5+" && c.rating < 4.5) return false;
    if (ratingFilter === "4.8+" && c.rating < 4.8) return false;

    return true;
  });

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleNext = () => {
    if (selectedIds.length > 0) {
      onNext(selectedIds);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2 text-[18px] font-semibold text-gray-900">
          <Sparkles className="h-5 w-5 text-blue-600" />
          <span>Select Contractors</span>
        </div>
        <div className="max-w-3xl text-sm text-gray-600">
          Browse and select contractors to suggest to homeowners
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-gray-900">Search And Filter</div>
          <div className="text-xs text-gray-500">
            Narrow contractors by profile, location, and contact details
          </div>
        </div>
        {selectedIds.length > 0 && (
          <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
            {selectedIds.length} selected
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex min-w-[260px] flex-1 flex-col gap-2">
          <label className="text-gray-600">Search</label>
          <div className="app-search-field">
            <Search className="app-field-icon" aria-hidden="true" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, phone, company..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm outline-none transition focus:border-blue-400 focus:bg-white"
            />
          </div>
        </div>

        {/* Profile */}
        <div className="flex flex-col gap-2">
          <label className="text-gray-600">Profile</label>
          <select
            value={profileFilter}
            onChange={(e) => setProfileFilter(e.target.value)}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            <option value="All">All Profiles</option>
            {allProfiles.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div className="flex flex-col gap-2">
          <label className="text-gray-600">Location</label>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            <option value="All">All Locations</option>
            {allLocations.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>

     

        {/* Continue Button */}
        {selectedIds.length > 0 && (
          <div className="ml-auto flex items-end">
            <button
              type="button"
              onClick={handleNext}
              className="contractor-primary-action rounded-xl border px-6 py-2.5 text-sm font-medium shadow-sm transition"
            >
              Continue ({selectedIds.length})
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Count */}
      <div className="mb-3 text-sm text-gray-600">
        {filteredContractors.length} contractor
        {filteredContractors.length !== 1 ? "s" : ""} found
      </div>

      {/* Loading */}
      {loading && <div role="status" aria-label="Loading contractors"><ContractorListSkeleton /></div>}

      {/* Grid */}
      {!loading && (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredContractors.map((c) => {
          const isSelected = selectedIds.includes(c.id);
          const serviceLabels = Array.isArray(c.services)
            ? c.services.map((service: any) =>
                typeof service === "string"
                  ? service
                  : service?.label || service?.name || "Service"
              )
            : [];
          return (
            <div
              key={c.id}
              className={`group flex h-full flex-col rounded-2xl border bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                isSelected ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-200"
              }`}
            >
              {/* Top */}
              <div className="mb-3 flex gap-3">
                <input
                  aria-label={`Select ${c.name}`}
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelection(c.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
                />

                {/* Avatar */}
                {c.logo ? (
                  <div className="flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
                    <ImageWithFallback
                      src={c.logo}
                      fallbackSrc={relibuildIcon}
                      alt={`${c.name || "Contractor"} logo`}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                ) : (
                  <div className="flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
                    <img src={relibuildIcon} alt="RELiBUILD" className="h-full w-full object-contain" />
                  </div>
                )}

                <div className="flex-1">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="font-semibold text-gray-900">{c.name}</div>
                    {c.isVerified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
                        <BadgeCheck className="h-3.5 w-3.5" />
                        Verified
                      </span>
                    )}
                  </div>

                  {c.profile && (
                    <div className="text-xs font-medium text-blue-600">
                      {c.profile}
                    </div>
                  )}

                  {c.companyName && (
                    <div className="mt-1.5 inline-flex items-center gap-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800">
                      <Building2 className="h-3.5 w-3.5" />
                      {c.companyName}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3 grid gap-1.5 rounded-xl bg-gray-50 p-3 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400" />
                  <span>{c.location || "Location not provided"}</span>
                </div>
                {c.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-gray-400" />
                    <span className="truncate">{c.email}</span>
                  </div>
                )}
                {c.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                    <span>{c.phone}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="mb-3 flex flex-wrap gap-2">
                {serviceLabels.slice(0, 4).map((serviceLabel: string, index: number) => (
                  <span
                    key={`${c.id}-service-${index}`}
                    className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700"
                  >
                    {serviceLabel}
                  </span>
                ))}
                {serviceLabels.length > 4 && (
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600">
                    +{serviceLabels.length - 4} more
                  </span>
                )}
              </div>

              {/* Stats */}
              {(c.yearsExperience > 0 || c.availability) && (
                <div className="mb-3 grid gap-1.5 rounded-xl border border-gray-100 p-3 text-sm">
                  {c.yearsExperience > 0 && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <BriefcaseBusiness className="h-4 w-4 text-blue-500" />
                      <span>{c.yearsExperience || 0} yrs experience</span>
                    </div>
                  )}
                  {c.availability && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <BadgeCheck className="h-4 w-4 text-emerald-500" />
                      <span>{c.availability}</span>
                    </div>
                  )}
                </div>
              )}
              

              {/* Button */}
              <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDetailContractor(c)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50"
                >
                  View Details
                </button>
                <button
                  type="button"
                  onClick={() => toggleSelection(c.id)}
                  className={`contractor-select-button rounded-xl border px-3 py-2.5 text-sm font-semibold transition${isSelected ? " is-selected" : ""}`}
                >
                  {isSelected ? "✓ Selected" : "Recommend"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Empty */}
      {!loading && filteredContractors.length === 0 && (
        <div className="py-12 text-center text-gray-600">
          No contractors found
        </div>
      )}

      {/* Modal */}
      {detailContractor && (
        <ContractorDetailModal
          contractor={detailContractor}
          isSelected={selectedIds.includes(detailContractor.id)}
          onToggleSelect={() => toggleSelection(detailContractor.id)}
          onClose={() => setDetailContractor(null)}
        />
      )}
    </div>
  );
}
